
import React, { useState, useEffect } from 'react';
import { isIOSDevice, isPwaMode } from "@/utils/pwaUtils";
import { Button } from "@/components/ui/button";
import { DebugControlPanel } from './DebugControlPanel';
import { LayerPositionIndicators } from './LayerPositionIndicators';
import { TouchPointVisualizer } from './TouchPointVisualizer';
import { DebugInfoPanel } from './DebugInfoPanel';
import { useDebugTouchPoints } from '@/hooks/debug/useDebugTouchPoints';
import { useLayerPositions } from '@/hooks/debug/useLayerPositions';
import { logger } from "@/utils/logger";

/**
 * Debug component to visualize touch events and element positions in iOS PWA mode
 * Only use during development to debug touch issues
 */
export const TouchDebugger: React.FC = () => {
  const [isActive, setIsActive] = useState(false);
  const isIOS = isIOSDevice();
  const isPwa = isPwaMode();
  const isIOSPwa = isIOS && isPwa;

  // Use custom hooks for touch points and layer position tracking
  const { points, handleTouchStart } = useDebugTouchPoints(isActive);
  const { elementPositions, analyzeLayerPositions } = useLayerPositions(isActive);

  // Log detection info on mount
  useEffect(() => {
    if (isIOSPwa) {
      logger.info(`[TouchDebugger] iOS PWA mode detected`);
    }
  }, [isIOSPwa]);

  useEffect(() => {
    if (!isActive) return;

    // Add touch debug class to body for visualization
    document.body.classList.add('touch-debug');
    document.body.classList.add('touch-debug-enabled');

    // Track touch events
    document.addEventListener('touchstart', handleTouchStart, { passive: false });

    // Analyze positions when debug is activated
    analyzeLayerPositions();

    // Reanalyze on resize or orientation change
    window.addEventListener('resize', analyzeLayerPositions);
    window.addEventListener('orientationchange', analyzeLayerPositions);

    return () => {
      document.body.classList.remove('touch-debug');
      document.body.classList.remove('touch-debug-enabled');
      document.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('resize', analyzeLayerPositions);
      window.removeEventListener('orientationchange', analyzeLayerPositions);
    };
  }, [isActive, handleTouchStart, analyzeLayerPositions]);

  // Function to reload the app and reset everything
  const handleResetApp = () => {
    try {
      logger.info('[TouchDebugger] App reset initiated');

      // Reload to ensure clean state
      window.location.reload();
    } catch (e) {
      logger.error('[TouchDebugger] Failed to reset app', e);
    }
  };

  if (!isIOSPwa) return null;

  return (
    <>
      <div className="fixed bottom-4 right-4 z-50">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsActive(!isActive)}
          className="bg-background/80 backdrop-blur-sm"
        >
          {isActive ? 'Disable' : 'Enable'} Debug
        </Button>
      </div>

      {isActive && (
        <div className="touch-debug-overlay">
          <DebugControlPanel
            currentOffset={null}
            adjustOffset={() => { }}
            resetOffset={handleResetApp}
          />

          <LayerPositionIndicators elementPositions={elementPositions} />

          <TouchPointVisualizer points={points} />

          <DebugInfoPanel
            elementPositions={elementPositions}
            currentOffset={null}
          />
        </div>
      )}
    </>
  );
};

export default TouchDebugger;
